import axios from "axios";
import { apiRequest } from "./client";

export type CompanyLookupSource = "internal" | "cvr" | "brreg" | "allabolag";

export interface CompanyLookupResult {
  source: CompanyLookupSource;
  company_name: string;
  address?: string;
  postal_code?: string;
  city?: string;
  country: string;
  website?: string;
}

/** Company countries offered by the signup and the admin "Add Joiner" form. */
export const COMPANY_COUNTRIES = [
  { code: "DK", name: "Denmark" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DE", name: "Germany" },
  { code: "GB", name: "United Kingdom" },
  { code: "FI", name: "Finland" },
] as const;

/** Human-readable name of the registry a lookup result came from. */
export const COMPANY_LOOKUP_SOURCE_LABEL: Record<CompanyLookupSource, string> = {
  internal: "BondIT registry",
  cvr: "Danish CVR registry",
  brreg: "Norwegian Brreg registry",
  allabolag: "allabolag.se (best effort)",
};

export const SUPPORTED_LOOKUP_COUNTRIES = ["DK", "NO", "SE"] as const;
export type LookupCountry = (typeof SUPPORTED_LOOKUP_COUNTRIES)[number];

export const isLookupSupported = (country: string): country is LookupCountry =>
  (SUPPORTED_LOOKUP_COUNTRIES as readonly string[]).includes(country);

const ORG_NUMBER_PATTERNS: Record<LookupCountry, RegExp> = {
  DK: /^\d{8}$/,
  NO: /^\d{9}$/,
  SE: /^\d{10}$/,
};

export const isValidOrgNumberFormat = (country: string, orgNumber: string): boolean => {
  if (!isLookupSupported(country)) {
    return false;
  }
  const normalised = orgNumber.replace(/[\s-]/g, "");
  return ORG_NUMBER_PATTERNS[country].test(normalised);
};

export const companyLookupApi = {
  /**
   * Look up a company by country + org number.
   * Returns null when not found (404). Throws on other errors.
   * Errors are suppressed from the global toast — caller renders inline feedback.
   */
  lookup: async (country: string, orgNumber: string): Promise<CompanyLookupResult | null> => {
    const params = new URLSearchParams({
      country,
      org_number: orgNumber.replace(/[\s-]/g, ""),
    });
    try {
      return await apiRequest.get<CompanyLookupResult>(
        `/public/consultant-application/company-lookup?${params.toString()}`,
        { suppressErrorToast: true }
      );
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },
};
